import * as Sentry from '@sentry/react';
import {
  getV1CompaniesCompanyIdForemen,
  GetV1CompaniesCompanyIdForemenData,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsers,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersTypeahead,
  getV1CompaniesCompanyIdUsers,
  GetV1CompaniesCompanyIdUsersData,
  getV1CompaniesCompanyIdUsersId,
  getV1CompaniesCompanyIdUsersTypeahead,
  GetV1CompaniesCompanyIdUsersTypeaheadData,
  getV1UsersMe,
  patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId,
  patchV1CompaniesCompanyIdUsersId,
  postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsers,
  postV1CompaniesCompanyIdUsers,
  postV1UsersUserIdDeviceTokens,
  putV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdInviteEmail,
  putV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdInviteSms,
  putV1CompaniesCompanyIdUsersUserIdInviteEmail,
  putV1CompaniesCompanyIdUsersUserIdInviteSms,
  User_Create_In_Company,
  User_Read,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useCallback, useState } from 'react';

import { UserNotificationsPreferenceDTO } from '~components/UserForm/UserNotificationsPreferenceForm';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { SystemRoles } from '~constants/enums';
import { Foreman } from '~hooks/useForeman/models';
import {
  AppearanceTheme,
  getSelectedNotificationMethodsByNotification,
  User,
} from '~hooks/useUsers/models';
import { Filters } from '~pages/Settings/UserManagement/hooks/useUserFilters';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { endUnauthenticatedSession } from '~services/sessionTokens';
import { useStores } from '~store';
import { isServerError } from '~utils/http';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { humanToSnakeCase } from '~utils/utilFunctions';

interface GetFilteredUsersParams {
  search?: string;
  access?:
    | 'execute_jobs'
    | 'manage_sites'
    | 'manage_materials'
    | 'manage_equipment'
    | 'manage_accounts'
    | 'manage_users';
  roleNames?: Array<SystemRoles>;
}

export default function useUsers() {
  const [isUpdating, setIsUpdating] = useState(false);
  const { userStore, jobStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
  const ownCompanyId = userStore.userCompany.id;

  // Async to make sure we have user on initial app load.
  // Retries on network errors (status 0 / no response) with exponential backoff,
  // since these are transient client-side failures (flaky network, VPN, tab sleep).
  const getUser = useCallback(async () => {
    const MAX_RETRIES = 3;
    const BACKOFF_MS = [1000, 2000, 4000];

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await getV1UsersMe();
        const parsedUser = User.parse(
          response.data.data,
          response.data.data.intercom_hash,
        );

        userStore.setUser(parsedUser);

        return parsedUser;
      } catch (error) {
        const isNetworkError =
          isAxiosError(error) && !error.response && error.code !== 'ERR_CANCELED';
        const hasRetriesLeft = attempt < MAX_RETRIES;

        if (isNetworkError && hasRetriesLeft) {
          await new Promise((resolve) => setTimeout(resolve, BACKOFF_MS[attempt]));
          continue;
        }

        // 401 means the token is expired/invalid — clear it and redirect to sign-in
        // so the user doesn't get stuck in a retry loop with a stale token.
        if (isAxiosError(error) && error.response?.status === 401) {
          endUnauthenticatedSession();

          return undefined;
        }

        // Report server errors (5xx) to Sentry for monitoring — HydrationErrorFallback
        // handles the recovery UX, but ops still needs visibility.
        if (isAxiosError(error) && error.response) {
          const status = error.response.status;

          if (isServerError(status)) {
            Sentry.captureException(error);
          }
        }

        return undefined;
      }
    }

    return undefined;
  }, [userStore]);

  const getUserById = async (
    id: string,
    companyId?: string,
    managedCompanyId?: string,
  ) => {
    try {
      if (companyId && managedCompanyId) {
        const response =
          await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId({
            path: {
              'company-id': companyId,
              'managed-company-id': managedCompanyId,
              id,
            },
          });
        return User.parse(response.data.data);
      }

      const response = await getV1CompaniesCompanyIdUsersId({
        path: { 'company-id': effectiveCompanyId, id },
      });
      return User.parse(response.data.data);
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.users.failed_to_fetch_selected'),
        );
      } catch {
        // Error already handled via toaster + Sentry.
      }

      return undefined;
    }
  };

  interface GetCompanyUsersProps {
    companyId: string;
    managedCompanyId?: string;
    limit?: number;
    link?: PaginationLink;
    cursor?: string;
    searchQuery?: string;
    filters?: Filters;
    signal?: AbortSignal;
  }

  interface GetCompanyForemenProps {
    companyId: string;
    limit?: number;
    link?: PaginationLink;
    cursor?: string;
    searchQuery?: string;
    signal?: AbortSignal;
  }

  interface GetCompanyUsersTypeaheadProps {
    companyId: string;
    managedCompanyId?: string;
    limit?: number;
    query?: string;
    cursor?: string;
    roleName?: SystemRoles;
  }

  const getCompanyUsers = async (options: GetCompanyUsersProps) => {
    const query: NonNullable<GetV1CompaniesCompanyIdUsersData['query']> = {
      'page[limit]': options.limit || MAX_API_PAGE_SIZE,
    };
    if (options.link && options.cursor) {
      query[`page[${options.link}]`] = options.cursor;
    }
    if (options.searchQuery) {
      query['search[datagrid]'] = options.searchQuery;
    }
    if (!isNil(options.filters)) {
      query['filter[iam_roles]'] = options.filters.roles;
    }
    if (!isNil(options.filters?.labels)) {
      query['filter[label_ids]'] = options.filters.labels;
    }

    const response = options.managedCompanyId
      ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsers({
          path: {
            'company-id': options.companyId,
            'managed-company-id': options.managedCompanyId,
          },
          query,
          signal: options.signal,
        })
      : await getV1CompaniesCompanyIdUsers({
          path: { 'company-id': options.companyId },
          query,
          signal: options.signal,
        });

    return {
      data: response.data.data.map((user) => User.parse(user)),
      pagination: extractPagination(response),
    };
  };

  const getCompanyForemen = async (options: GetCompanyForemenProps) => {
    const query: NonNullable<GetV1CompaniesCompanyIdForemenData['query']> = {
      'page[limit]': options.limit || MAX_API_PAGE_SIZE,
    };
    if (options.link && options.cursor) {
      query[`page[${options.link}]`] = options.cursor;
    }
    if (options.searchQuery) {
      query['search[datagrid]'] = options.searchQuery;
    }

    const response = await getV1CompaniesCompanyIdForemen({
      path: { 'company-id': options.companyId },
      query,
      signal: options.signal,
    });

    return {
      data: response.data.data.map((foreman) => Foreman.parse(foreman)),
      pagination: extractPagination(response),
    };
  };

  const getCompanyUsersTypeahead = async (options: GetCompanyUsersTypeaheadProps) => {
    const query: NonNullable<GetV1CompaniesCompanyIdUsersTypeaheadData['query']> = {
      'page[limit]': options.limit ?? MAX_API_PAGE_SIZE,
    };

    if (options.roleName) {
      query['filter[iam_roles]'] = [humanToSnakeCase(options.roleName)];
    }

    if (options.query) {
      query['search[query]'] = options.query;
    }

    if (options?.cursor) {
      query['page[after]'] = options.cursor;
    }

    try {
      const response = options.managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersTypeahead({
            path: {
              'company-id': options.companyId,
              'managed-company-id': options.managedCompanyId,
            },
            query,
          })
        : await getV1CompaniesCompanyIdUsersTypeahead({
            path: { 'company-id': options.companyId },
            query,
          });

      const users = response.data.data.map((user) => User.parse(user as User_Read));
      const pagination = extractPagination(response);

      return { data: users, pagination };
    } catch (e) {
      try {
        connection.handleRequestError(e, t('error_messages.users.failed_to_fetch'));
      } catch {
        // Error already handled via toaster + Sentry.
      }

      return {
        data: [],
        pagination: { page: 1, limit: options.limit ?? MAX_API_PAGE_SIZE },
      };
    }
  };

  const getFilteredCompanyUsers = async (
    companyId: string,
    options: GetFilteredUsersParams,
  ): Promise<User[]> => {
    const params: NonNullable<GetV1CompaniesCompanyIdUsersData['query']> = {
      'page[limit]': MAX_API_PAGE_SIZE,
      'filter[access]': options.access,
      'filter[iam_roles]': options.roleNames?.map((role) => humanToSnakeCase(role)),
    };

    if (options.search) {
      params['search[datagrid]'] = options.search;
    }

    try {
      const response = await getV1CompaniesCompanyIdUsers({
        path: { 'company-id': companyId },
        query: params,
      });
      const users = response.data.data.map((user) => User.parse(user));

      if (options.access === 'execute_jobs') {
        jobStore.setDrivers(companyId, users);
      }

      return users;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.users.failed_to_fetch'));
      throw error;
    }
  };

  const updateUser = async (
    userData: User,
    userNotificationsPreferenceData?: UserNotificationsPreferenceDTO,
    companyId?: string,
    managedCompanyId?: string,
  ): Promise<User> => {
    setIsUpdating(true);

    try {
      let user: User;

      if (companyId && managedCompanyId) {
        const response =
          await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId({
            path: {
              'company-id': companyId,
              'managed-company-id': managedCompanyId,
              id: userData.id,
            },
            body: User.deparse(
              userData,
              undefined,
              userNotificationsPreferenceData,
            ) as Parameters<
              typeof patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId
            >[0]['body'],
          });
        user = User.parse(response.data.data);
      } else {
        const response = await patchV1CompaniesCompanyIdUsersId({
          path: {
            'company-id': companyId || effectiveCompanyId,
            id: userData.id,
          },
          body: User.deparse(
            userData,
            undefined,
            userNotificationsPreferenceData,
          ) as Parameters<typeof patchV1CompaniesCompanyIdUsersId>[0]['body'],
        });
        user = User.parse(response.data.data);
      }

      userStore.updateUser(user);

      return user;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.users.failed_to_update'));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  // Focused persistence for the dark-mode selector: PATCHes only the appearance
  // sub-key. The backend merges preferences per sub-key, so this never clears
  // the user's notification preferences.
  const updateUserAppearanceTheme = async (userId: string, theme: AppearanceTheme) => {
    try {
      // TODO(TRE-15792): drop the body cast once @treadinc/horizon-api-spec
      // publishes UserPreferences.appearance (BE TRE-15791).
      await patchV1CompaniesCompanyIdUsersId({
        path: { 'company-id': ownCompanyId, id: userId },
        body: {
          preferences: { appearance: { theme } },
        } as Parameters<typeof patchV1CompaniesCompanyIdUsersId>[0]['body'],
      });
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.users.failed_to_update'));
      throw error;
    }
  };

  const updateUserNotificationPreferences = useCallback(
    async (
      userId: string,
      userNotificationsPreferenceData: Partial<UserNotificationsPreferenceDTO>,
    ) => {
      try {
        const response = await patchV1CompaniesCompanyIdUsersId({
          path: { 'company-id': ownCompanyId, id: userId },
          body: {
            preferences: {
              notifications: getSelectedNotificationMethodsByNotification(
                userNotificationsPreferenceData,
              ),
            },
          },
        });
        userStore.updateUser(User.parse(response.data.data));
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.users.failed_to_update'));
        throw error;
      }
    },
    [ownCompanyId],
  );

  const createUser = async (
    userData: User,
    invitesToSend: User_Create_In_Company['send_invite'] = {},
    companyId?: string,
    managedCompanyId?: string,
  ): Promise<User> => {
    setIsUpdating(true);

    try {
      let user: User;

      if (companyId && managedCompanyId) {
        const response =
          await postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsers({
            path: {
              'company-id': companyId,
              'managed-company-id': managedCompanyId,
            },
            body: User.deparse(userData, invitesToSend) as Parameters<
              typeof postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsers
            >[0]['body'],
          });
        user = User.parse(response.data.data);
      } else {
        const response = await postV1CompaniesCompanyIdUsers({
          path: {
            'company-id': companyId || userData.company?.id || effectiveCompanyId,
          },
          body: User.deparseForCompanyCreate(userData, invitesToSend),
        });
        user = User.parse(response.data.data);
      }

      return user;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.users.failed_to_create'));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const sendInvitation = async (
    id: string,
    method: 'email' | 'sms',
    companyId?: string,
    managedCompanyId?: string,
  ) => {
    setIsUpdating(true);

    try {
      if (companyId && managedCompanyId) {
        if (method === 'email') {
          await putV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdInviteEmail(
            {
              path: {
                'company-id': companyId,
                'managed-company-id': managedCompanyId,
                'user-id': id,
              },
            },
          );
        } else {
          await putV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdInviteSms(
            {
              path: {
                'company-id': companyId,
                'managed-company-id': managedCompanyId,
                'user-id': id,
              },
            },
          );
        }
      } else {
        if (method === 'email') {
          await putV1CompaniesCompanyIdUsersUserIdInviteEmail({
            path: { 'company-id': companyId || effectiveCompanyId, 'user-id': id },
          });
        } else {
          await putV1CompaniesCompanyIdUsersUserIdInviteSms({
            path: { 'company-id': companyId || effectiveCompanyId, 'user-id': id },
          });
        }
      }
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.users.failed_to_send_invite', { method }),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const addUserDeviceToken = async (token: string) => {
    try {
      const response = await postV1UsersUserIdDeviceTokens({ body: { token } });

      return response.data;
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.users.failed_to_add_device_token'),
        );
      } catch {
        // Error already handled via toaster + Sentry.
      }

      return undefined;
    }
  };

  return {
    addUserDeviceToken,
    createUser,
    getCompanyForemen,
    getCompanyUsers,
    getCompanyUsersTypeahead,
    getFilteredCompanyUsers,
    getUser,
    getUserById,
    isUpdating,
    sendInvitation,
    updateUser,
    updateUserAppearanceTheme,
    updateUserNotificationPreferences,
  };
}
